import { Num, T } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import { AgentMonitorTaskPanel } from '../../agent-monitor/components/AgentMonitorTaskPanel';
import {
  AGENT_PAGE_TASKS_ID,
  isAgentPageWorkPane,
  type AgentPageWorkPane,
} from '../agentPageRuns';
import type { AgentPageRuns } from '../useAgentPageRuns';
import { AgentRunsSection } from './AgentRunsSection';
import { AgentTaskQueuePanel } from './AgentTaskQueuePanel';

/**
 * History + the selected run, or (for queue-driven agents) that same block
 * sharing a tab with the task list so the page does not grow another section.
 */
export function AgentPageWorkSection({
  pane,
  onPaneChange,
  showTasks,
  openTaskCount,
  latestRun,
  history,
  agentDefinitionId,
  agentName,
}: {
  pane: AgentPageWorkPane;
  onPaneChange: (pane: AgentPageWorkPane) => void;
  showTasks: boolean;
  openTaskCount: number;
  latestRun: AgentRunSummary | null;
  /** The History list: its runs, paging and filter (see `useAgentPageRuns`). */
  history: AgentPageRuns['history'];
  agentDefinitionId: string;
  agentName: string;
}) {
  const runsSection = (
    <AgentRunsSection
      history={history}
      agentDefinitionId={agentDefinitionId}
      agentName={agentName}
    />
  );

  if (!showTasks) return runsSection;

  return (
    <section id={AGENT_PAGE_TASKS_ID} className="scroll-mt-4">
      <Tabs
        value={pane}
        onValueChange={(value) => {
          if (isAgentPageWorkPane(value)) onPaneChange(value);
        }}
        className="flex min-h-0 flex-col gap-3"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="runs">
            <T context="agent page host tab for history and the selected run">
              Run details
            </T>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-1.5">
            <T context="agent page host tab for the agent's task queue">
              Tasks
            </T>
            {openTaskCount > 0 && (
              <span className="tabular-nums text-muted-foreground">
                <Num>{openTaskCount}</Num>
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="agent-page-queue-tab">
            <T context="agent page host tab for the agent's upcoming tasks in the order it will work them">
              Queue
            </T>
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="runs"
          forceMount
          className="mt-0 data-[hidden]:hidden"
        >
          {runsSection}
        </TabsContent>
        <TabsContent
          value="tasks"
          forceMount
          className="mt-0 h-[min(36rem,70vh)] data-[hidden]:hidden"
        >
          <AgentMonitorTaskPanel
            agentDefinitionId={agentDefinitionId}
            latestRun={latestRun}
            autoScrollOnRunning={false}
          />
        </TabsContent>
        <TabsContent
          value="queue"
          className="mt-0 h-[min(36rem,70vh)] data-[hidden]:hidden"
        >
          {pane === 'queue' && (
            <AgentTaskQueuePanel agentDefinitionId={agentDefinitionId} />
          )}
        </TabsContent>
      </Tabs>
    </section>
  );
}
